import {
  analyticsPageUrl,
  analyticsReferrer,
} from "@tabbio/utils/analytics/privacy";
import {
  createBrowserSession,
  SESSION_IDLE_MS,
  SESSION_MAX_MS,
  uuidV7Timestamp,
  uuidV7,
  type SessionStorage,
} from "@tabbio/utils/analytics/browser-session";

export type BrowserPageProperties = Record<string, unknown>;
/** Small, SDK-free session/page lifecycle shared by marketing and app-web. */
export function createBrowserMeasurement(options: {
  storage: SessionStorage;
  location(): string;
  referrer(): string;
  scroll(): { top: number; height: number; viewport: number };
  now?: () => number;
}) {
  const now = options.now ?? Date.now;
  const session = createBrowserSession({ storage: options.storage, now });
  let page: {
    url: string;
    startedAt: number;
    lastActivityAt: number;
    properties: BrowserPageProperties;
    maxScroll: number;
    maxContent: number;
    maxTop: number;
    maxHeight: number;
  } | null = null;
  function common(
    properties: BrowserPageProperties = {},
  ): BrowserPageProperties {
    const current = session.snapshot();
    if (page?.properties.$session_id === current.id)
      page.lastActivityAt = now();
    const safeUrl = analyticsPageUrl(options.location());
    const url = safeUrl ? new URL(safeUrl) : null;
    return {
      ...properties,
      $session_id: current.id,
      $current_url: safeUrl ?? "",
      $host: url?.hostname ?? "",
      $pathname: url?.pathname ?? "",
      $referrer: analyticsReferrer(options.referrer()) ?? "",
      $referring_domain: analyticsReferrer(options.referrer())
        ? new URL(options.referrer()).hostname
        : "$direct",
      $pageview_id:
        page?.properties.$session_id === current.id
          ? page.properties.$pageview_id
          : uuidV7(now()),
    };
  }
  function updateScroll() {
    if (!page) return;
    if (session.snapshot().id === page.properties.$session_id)
      page.lastActivityAt = now();
    const { top, height, viewport } = options.scroll();
    const safeTop = Math.max(0, top);
    const fraction = (value: number) => Math.min(1, Math.max(0, value));
    page.maxTop = Math.max(page.maxTop, safeTop);
    page.maxHeight = Math.max(page.maxHeight, height);
    page.maxScroll = Math.max(
      page.maxScroll,
      height > viewport ? fraction(safeTop / (height - viewport)) : 1,
    );
    page.maxContent = Math.max(
      page.maxContent,
      height > 0 ? fraction((safeTop + viewport) / height) : 1,
    );
  }
  function leave(): BrowserPageProperties | null {
    if (!page) return null;
    const startedAt = uuidV7Timestamp(String(page.properties.$session_id));
    if (
      startedAt === null ||
      now() - startedAt >= SESSION_MAX_MS ||
      now() - page.lastActivityAt >= SESSION_IDLE_MS
    ) {
      page = null;
      return null;
    }
    const result = {
      ...page.properties,
      $prev_pageview_id: page.properties.$pageview_id,
      $prev_pageview_pathname: page.properties.$pathname,
      $prev_pageview_duration: Math.max(0, now() - page.startedAt) / 1000,
      $prev_pageview_max_scroll_percentage: page.maxScroll,
      $prev_pageview_max_content_percentage: page.maxContent,
      $prev_pageview_max_scroll: page.maxTop,
      $prev_pageview_max_content: page.maxHeight,
    };
    page = null;
    return result;
  }
  function view(properties: BrowserPageProperties = {}): {
    pageview: BrowserPageProperties | null;
    pageleave: BrowserPageProperties | null;
  } {
    const context = common(properties);
    if (
      page &&
      page.url === context.$current_url &&
      page.properties.$session_id === context.$session_id
    )
      return { pageview: null, pageleave: null };
    const pageleave = leave();
    const pageview = { ...context, $pageview_id: uuidV7(now()) };
    page = {
      url: String(context.$current_url),
      startedAt: now(),
      lastActivityAt: now(),
      properties: pageview,
      maxScroll: 0,
      maxContent: 0,
      maxTop: 0,
      maxHeight: 0,
    };
    updateScroll();
    return { pageview, pageleave };
  }
  return {
    sessionId: () => session.current()?.id,
    properties: common,
    view,
    leave,
    updateScroll,
    reset() {
      page = null;
      session.reset();
    },
  };
}

/** No raw performance entries: those contain resource URLs and DOM text. */
export function postHogWebVital(
  properties: Record<string, unknown>,
): BrowserPageProperties | null {
  const name = properties.metric_name;
  const value = properties.metric_value;
  if (
    typeof name !== "string" ||
    !["LCP", "CLS", "INP", "FCP", "TTFB"].includes(name) ||
    typeof value !== "number" ||
    !Number.isFinite(value)
  )
    return null;
  return {
    [`$web_vitals_${name}_value`]: value,
    [`$web_vitals_${name}_event`]: {
      name,
      value,
      ...(typeof properties.metric_id === "string"
        ? { id: properties.metric_id.slice(0, 100) }
        : {}),
      ...(["good", "needs-improvement", "poor"].includes(
        String(properties.metric_rating),
      )
        ? { rating: properties.metric_rating }
        : {}),
    },
  };
}
